import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronUp, Send, AlertCircle, ArrowLeft, FileImage, UserPlus } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";
import CaseFilePanel from "../components/CaseFilePanel";
import FilingTrackerPanel from "../components/FilingTrackerPanel";
import InviteManagerModal from "../components/InviteManagerModal";
import { launchStripeCheckout, pollPaymentStatus, clearSessionFromUrl } from "../payment";
import VoiceRecorder from "../components/VoiceRecorder";
import MultiSlotUploadWidget from "../components/MultiSlotUploadWidget";
import BatchedFieldFormWidget from "../components/BatchedFieldFormWidget";
import EntityTypeConfirmWidget from "../components/EntityTypeConfirmWidget";
import AuthenticityAffirmWidget from "../components/AuthenticityAffirmWidget";
import {
  RadioWidget, FileUploadWidget, DatePickerWidget, StakeholderConfirmationCard,
  ConsentWidget,
} from "../components/ChatWidgets";

// Strip the JSON widget directive (assistant) and the INFIN_INTERNAL marker
// (either role) so neither shows in chat bubbles or in persisted history.
function cleanMessage(text) {
  if (!text) return "";
  return text
    .replace(/```json\s*\{[\s\S]*?\}\s*```/g, "")
    .replace(/<!--\s*INFIN_INTERNAL[\s\S]*?-->/g, "")
    .trim();
}

// Phase 2 — right-hand panel swap. Post-PAID statuses show the filing
// tracker; everything else (DRAFT, INTAKE_*, AWAITING_PAYMENT, ON_HOLD,
// ABANDONED) shows the existing intake panel. Strict mutual exclusion.
const POST_PAID_STATUSES = new Set(["PAID_HANDED_OFF", "IN_FILING", "INCORPORATED"]);
function isPostPaid(caseData) {
  return POST_PAID_STATUSES.has(caseData?.case_meta?.status);
}

function activeStakeholderId(caseData) {
  // Heuristic: latest unconfirmed stakeholder
  const arr = caseData?.stakeholders || [];
  const un = arr.filter((s) => !s.user_confirmed);
  if (un.length) return un[un.length - 1].stakeholder_id;
  return arr[arr.length - 1]?.stakeholder_id;
}

export default function ChatPage() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [widget, setWidget] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const scrollRef = useRef(null);
  const greetSentRef = useRef(false);

  useEffect(() => { (async () => {
    try {
      const [c, hist] = await Promise.all([api.getCase(caseId), api.getChatHistory(caseId)]);
      setCaseData(c);
      const visible = hist.map((m, i) => ({
        id: `h-${i}`, role: m.role,
        text: cleanMessage(m.text),
      })).filter((m) => m.text);
      setMessages(visible);
      // If history is empty, send a synthetic greet that includes the user's display name.
      // Guard with a ref so StrictMode's double-invocation (or any re-mount) cannot fire it twice.
      if (visible.length === 0 && !greetSentRef.current) {
        greetSentRef.current = true;
        const greet = user?.display_name
          ? `Hi Infi — I'm ${user.display_name}, ready to start my Private Limited incorporation.`
          : `Hi Infi — I'm ready to start.`;
        await runTurn(greet, { synthetic: true });
      } else if (visible.length > 0) {
        // History already exists — hydrate the pending widget from the
        // orchestrator so the founder lands back on confirmation_card,
        // field_form, affirm widget, etc. after reload. The chat-turn
        // path is the only other place widgets are set, and we don't
        // burn an LLM turn on a passive page load.
        try {
          const w = await api.getCurrentWidget(caseId);
          setWidget(w?.widget || null);
        } catch (e) {
          // Widget hydration is non-fatal — the case file loaded; user
          // can still click into a widget on their next chat turn.
          // Log for debugging so a silent regression in /widget shows up.
          console.warn("[ChatPage] widget hydration failed:", e);
        }
      }
    } catch (e) {
      toast.error("Could not load case");
      nav("/dashboard");
    }
  })(); // run once on mount
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stripe return path — if Stripe redirected us back to
  // /cases/<id>?session_id=cs_test_…, poll the backend until the
  // session resolves and refresh the case file when it does.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid || !caseId) return;
    let cancelled = false;
    (async () => {
      toast.info("Payment received — confirming with the gateway…");
      const result = await pollPaymentStatus(caseId, sid);
      if (cancelled) return;
      clearSessionFromUrl();
      if (result.ok) {
        try {
          const fresh = await api.getCase(caseId);
          setCaseData(fresh);
          toast.success("Payment confirmed. Your incorporation has started.");
        } catch {
          toast.success("Payment confirmed.");
        }
      } else if (result.timeout) {
        toast.info("Still confirming with the gateway — refresh in a few seconds if the case file hasn't updated.");
      } else {
        const msg = result.error
          || "Payment didn't complete. If you were charged, please contact support.";
        setError(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loadingMsg]);

  // Phase 4.6 — "Respond via Infi" affordance on the filing tracker fires
  // a window event that we listen for here. It injects a synthetic opener
  // referencing the stage so Infi enters filing-response mode immediately.
  useEffect(() => {
    const handler = (e) => {
      const stage = e?.detail?.stage;
      if (!stage) return;
      runTurn(`I'd like to respond to the team's request on the "${stage}" stage.`);
    };
    window.addEventListener("infin:respond", handler);
    return () => window.removeEventListener("infin:respond", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function runTurn(message, opts = {}) {
    if (!message || sending) return;
    setError(null);
    setWidget(null);
    setSending(true);
    setLoadingMsg("Infi is thinking…");
    if (!opts.synthetic) {
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text: cleanMessage(message) }]);
    }
    try {
      const resp = await api.sendChat(caseId, message);
      const calls = resp.tool_calls_made || [];
      // Update loading hint based on tool calls
      if (calls.some((t) => t.name === "extract_document")) {
        setLoadingMsg("Reading your document…");
      }
      const failedCall = calls.find((t) => !t.ok);
      if (failedCall) {
        // Never show the tool name or the raw error code to the user.
        setError("I couldn't complete that step just now — please try again.");
      }
      setCaseData(resp.case);
      setMessages((m) => [...m, {
        id: `a-${Date.now()}`, role: "assistant",
        text: cleanMessage(resp.assistant_message || ""),
      }]);
      setWidget(resp.widget || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Something went wrong";
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
      setLoadingMsg("");
    }
  }

  async function handleUpload(file, target, params) {
    if (!file) return;
    // WO 5 — INC-3 single upload is a deterministic backend attach,
    // NOT a vision-extract turn. Detect by target and route to the
    // dedicated handler.
    if (target === "nominee:inc3_consent") {
      return handleInc3Upload(file, params || {});
    }
    setUploading(true);
    setLoadingMsg("Uploading…");
    try {
      const { file_id } = await api.uploadFile(caseId, file);
      // Tell Infi. Human-friendly text is what shows in the chat bubble and in
      // persisted history; the technical routing block sits inside an
      // <!--INFIN_INTERNAL ... --> marker which is stripped from every render
      // surface (see cleanMessage) and is something the system prompt forbids
      // Infi from repeating.
      const sid = activeStakeholderId(caseData);
      const internal = (tool, extra = "") =>
        `\n\n<!--INFIN_INTERNAL ${tool} file_id=${file_id}${sid ? ` stakeholder_id=${sid}` : ""}${extra}-->`;
      let msg;
      if (target === "stakeholder:pan") {
        msg = `I've uploaded my PAN card.${internal("extract_document", " expected_type=PAN")}`;
      } else if (target === "stakeholder:aadhaar") {
        msg = `I've uploaded my Aadhaar card.${internal("extract_document", " expected_type=AADHAAR")}`;
      } else if (target === "stakeholder:photo") {
        msg = `I've uploaded my photograph.${internal("attach_photo")}`;
      } else if (target === "company:address_proof") {
        msg = `I've uploaded the registered-office address proof.${internal("attach_address_proof", " target=company.address_proof")}`;
      } else {
        msg = `I've uploaded a file.${internal("attach_file", ` target=${target}`)}`;
      }
      await runTurn(msg);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      setLoadingMsg("");
    }
  }

  async function handleMultiUpload(picks, params) {
    // picks: { [slot]: File[] }
    // params: { stakeholder_id, subject_ref, slots: [...] }
    setUploading(true);
    setLoadingMsg("Uploading documents…");
    try {
      // Flatten + tag every file with its slot.
      const flat = [];
      for (const [slot, files] of Object.entries(picks || {})) {
        for (const f of files) flat.push({ slot, file: f });
      }
      // Upload all in one batch (resilient: server validates pre-write).
      const onlyFiles = flat.map((x) => x.file);
      let resp;
      try {
        resp = await api.uploadFilesBatch(caseId, onlyFiles);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Upload failed";
        toast.error(msg);
        return;
      }
      const uploaded = resp.files || [];
      if (uploaded.length !== flat.length) {
        toast.error("Some files did not upload — please try again.");
        return;
      }
      // Group file_ids by slot (Aadhaar may have 2).
      const bySlot = {};
      uploaded.forEach((u, i) => {
        const slot = flat[i].slot;
        (bySlot[slot] ||= []).push({ file_id: u.file_id, name: u.original_filename });
      });
      // Build a human-readable line + internal routing block for Infi.
      const sid = params.stakeholder_id || activeStakeholderId(caseData);
      const slotsCovered = Object.keys(bySlot).sort();
      const human =
        `I've uploaded ${slotsCovered.length === 1
          ? slotsCovered[0].replace("_proof", "").replace(/_/g, " ")
          : "my PAN, Aadhaar, and photograph"} for ${params.subject_ref || "this director"}.`;
      const internal =
        `<!--INFIN_INTERNAL multi_slot_upload stakeholder_id=${sid} ` +
        Object.entries(bySlot)
          .map(([slot, arr]) => `${slot}=${arr.map((a) => a.file_id).join(",")}`)
          .join(" ") +
        " -->";
      await runTurn(`${human}\n\n${internal}`);
    } finally {
      setUploading(false);
      setLoadingMsg("");
    }
  }

  async function handleBatchedFieldSubmit(form, params) {
    // form: { [path]: value }
    setSending(true);
    setLoadingMsg("Saving…");
    try {
      const sid = params.stakeholder_id || activeStakeholderId(caseData);
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        // Strip leading "stakeholders." for the upsert payload.
        const sk = k.startsWith("stakeholders.") ? k.slice("stakeholders.".length) : k;
        payload[sk] = v;
      }
      await runTurn(
        `Here are the remaining details for ${params.subject_ref || sid}: ${JSON.stringify(payload)}.` +
        `\n\n<!--INFIN_INTERNAL upsert_stakeholder stakeholder_id=${sid} fields_json=${JSON.stringify(payload)} -->`
      );
    } finally {
      setSending(false);
      setLoadingMsg("");
    }
  }

  async function handleEntityTypeConfirm(entity_type) {
    // WO 5 — bypass the LLM entirely. Confirm deterministically, then
    // refresh the case so the orchestrator advances to the next widget
    // (multi-slot tray for member or Pvt Ltd Director 1).
    setSending(true);
    setLoadingMsg("Locking choice…");
    try {
      await api.confirmEntityType(caseId, entity_type);
      const friendly = entity_type === "OPC"
        ? "I'd like to register as a One Person Company (OPC)."
        : "I'd like to register as a Private Limited Company.";
      await runTurn(friendly);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not confirm entity type";
      toast.error(msg);
      setError(msg);
    } finally {
      setSending(false);
      setLoadingMsg("");
    }
  }

  async function handleInc3Upload(file, params) {
    // WO 5 — INC-3 nominee consent. Single PDF/image upload, no OCR.
    // Calls the deterministic /inc3-consent endpoint, NOT the LLM.
    if (!file) return;
    setUploading(true);
    setLoadingMsg("Uploading INC-3 consent…");
    try {
      const { file_id } = await api.uploadFile(caseId, file);
      await api.attachInc3Consent(caseId, file_id, params.stakeholder_id);
      await runTurn("I've uploaded the INC-3 nominee consent form.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not attach INC-3";
      toast.error(msg);
    } finally {
      setUploading(false);
      setLoadingMsg("");
    }
  }

  async function handleAffirmReupload(params) {
    // WO 6 — founder picked "Re-upload this document" on the affirm
    // widget. Clear the blocked_uploads entry deterministically, then
    // trigger a quiet turn so the orchestrator returns to the
    // multi-slot tray.
    setSending(true);
    setLoadingMsg("Clearing the blocked upload…");
    try {
      await api.discardBlockedUpload(caseId, params.file_id);
      await runTurn("Let me try uploading that document again.");
    } catch (e) {
      const msg = e?.response?.data?.detail
                || "Could not clear the upload";
      toast.error(msg);
    } finally {
      setSending(false);
      setLoadingMsg("");
    }
  }

  async function handleAffirmUseAnyway(params, reason) {
    // WO 6 — founder picked "Use this document anyway" with a typed
    // reason. Backend records the override AND re-runs extract so
    // fields persist with USER_INSISTED.
    setSending(true);
    setLoadingMsg("Recording your confirmation…");
    try {
      await api.recordAuthenticityOverride(caseId, params.file_id, {
        reason,
        stakeholder_id: params.stakeholder_id,
        slot: params.slot,
        flag_names: params.flag_names,
      });
      await runTurn(
        "I've confirmed that this is my original document — please proceed with it."
      );
    } catch (e) {
      const msg = e?.response?.data?.detail
                || "Could not record the confirmation";
      toast.error(msg);
    } finally {
      setSending(false);
      setLoadingMsg("");
    }
  }

  function renderWidget() {
    if (!widget || sending) return null;
    const type = widget.type || widget.widget; // tolerate legacy shape
    const params = widget.params || widget;
    if (type === "consent") {
      return <ConsentWidget
        caseId={caseId}
        onConsented={() => {
          setWidget(null);
          api.getCase(caseId).then(setCaseData).catch(() => {});
          runTurn("I have read the consent notice and I agree. Please proceed.");
        }}
      />;
    }
    if (type === "entity_type_confirm") {
      return <EntityTypeConfirmWidget
        options={params.options || []}
        busy={sending}
        onSelect={handleEntityTypeConfirm}
      />;
    }
    if (type === "authenticity_affirm") {
      return <AuthenticityAffirmWidget
        subjectRef={params.subject_ref}
        fileId={params.file_id}
        slot={params.slot}
        flagNames={params.flag_names || []}
        stakeholderId={params.stakeholder_id}
        busy={sending}
        onReupload={() => handleAffirmReupload(params)}
        onAffirm={(reason) => handleAffirmUseAnyway(params, reason)}
      />;
    }
    if (type === "multi_slot_upload") {
      return <MultiSlotUploadWidget
        subjectRef={params.subject_ref}
        slots={params.slots || []}
        busy={uploading}
        onSubmit={(picks) => handleMultiUpload(picks, params)}
      />;
    }
    if (type === "radio") {
      return <RadioWidget options={params.options || []}
                          onSelect={(opt) => { setWidget(null); runTurn(opt); }} />;
    }
    if (type === "single_upload" || type === "file_upload") {
      return <FileUploadWidget
        accept={params.accept}
        target={params.target}
        busy={uploading}
        onUpload={(file, tgt) => handleUpload(file, tgt, params)} />;
    }
    if (type === "date_picker") {
      return <DatePickerWidget onPick={(d) => { setWidget(null); runTurn(d); }} />;
    }
    if (type === "field_form") {
      return <BatchedFieldFormWidget
        subjectRef={params.subject_ref}
        fields={params.fields || []}
        busy={sending}
        onSubmit={(form) => handleBatchedFieldSubmit(form, params)}
      />;
    }
    if (type === "confirmation_card") {
      const sid = params.stakeholder_id || activeStakeholderId(caseData);
      const s = (caseData?.stakeholders || []).find((x) => x.stakeholder_id === sid);
      if (!s) return null;
      return <StakeholderConfirmationCard
        stakeholder={s}
        onConfirm={(form) => {
          setWidget(null);
          const payload = JSON.stringify(form);
          runTurn(
            `I've reviewed the stakeholder card for ${sid}. Please upsert_stakeholder with stakeholder_id='${sid}' and these corrected fields, then set user_confirmed=true and run validation. Corrections: ${payload}`
          );
        }}
        onCancel={() => setWidget(null)}
      />;
    }
    return null;
  }

  if (!caseData) {
    return <div className="h-screen flex items-center justify-center text-slate-500">Loading case…</div>;
  }

  return (
    <div className="h-screen bg-[var(--canvas)] flex flex-col md:flex-row overflow-hidden">
      {/* CHAT PANE */}
      <div className="flex-1 md:w-3/5 md:border-r md:border-slate-200 flex flex-col min-h-0">
        {/* Header */}
        <div className="border-b border-slate-200 bg-white px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/dashboard")} data-testid="back-btn"
                    className="text-slate-500 hover:text-slate-900">
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">Infi</div>
              <div className="text-xs text-slate-500">Incorporation assistant</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 truncate max-w-[320px] flex items-center gap-3">
            <button
              onClick={() => setInviteModalOpen(true)}
              data-testid="open-invite-manager-btn"
              className="inline-flex items-center gap-1.5 text-[#C5283F] hover:text-[#A82035] font-medium border border-slate-300 px-2.5 py-1 rounded-md"
            >
              <UserPlus size={12} /> Invite co-founder
            </button>
            {user?.picture && (
              <img src={user.picture} alt="" data-testid="chat-user-avatar"
                   className="w-7 h-7 rounded-full ring-1 ring-slate-200 object-cover" />
            )}
            <span className="hidden sm:inline">{user?.email}</span>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
                    className="underline hover:text-slate-900">Logout</button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-4 sm:px-6 py-6 space-y-3"
             data-testid="chat-scroll">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} infin-fade-up`}>
              <div
                data-testid={`msg-${m.role}`}
                className={[
                  "max-w-[88%] sm:max-w-[80%] px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap",
                  m.role === "user"
                    ? "bg-[#C5283F] text-white rounded-2xl rounded-tr-sm shadow-sm"
                    : "bg-white text-slate-900 border border-slate-200 rounded-2xl rounded-tl-sm shadow-sm",
                ].join(" ")}
              >
                {m.text}
              </div>
            </div>
          ))}

          {/* Inline widget under last assistant message */}
          {widget && messages.length > 0 && messages[messages.length - 1].role === "assistant" && (
            <div className="flex justify-start">
              <div className="max-w-[88%] sm:max-w-[80%] w-full">
                {renderWidget()}
              </div>
            </div>
          )}

          {/* Typing indicator */}
          {sending && (
            <div className="flex justify-start infin-fade-up">
              <div className="bg-white border border-slate-200 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm text-slate-500 inline-flex items-center gap-2">
                <span className="infin-typing-dot" />
                <span className="infin-typing-dot" />
                <span className="infin-typing-dot" />
                <span>{loadingMsg || "Infi is thinking…"}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-start">
              <div className="bg-red-50 text-red-800 border border-red-200 px-4 py-2.5 rounded-lg text-sm flex items-start gap-2 max-w-[88%]"
                   data-testid="chat-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3">
          <form
            onSubmit={(e) => { e.preventDefault(); const v = input.trim(); if (!v) return; setInput(""); runTurn(v); }}
            className="flex items-end gap-2"
          >
            <textarea
              data-testid="chat-input"
              value={input}
              rows={1}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // WO 10 — Enter submits, Shift+Enter inserts a newline.
                // The single-line input has been replaced with a textarea
                // so the founder can compose multi-line messages (e.g.
                // a paragraph of business activity) without losing them
                // to an accidental submit. IME composition (CJK / mobile
                // keyboards) is respected — never submit during composition.
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  const v = input.trim();
                  if (!v || sending) return;
                  setInput("");
                  runTurn(v);
                }
              }}
              disabled={sending}
              placeholder="Type your message…   (Shift+Enter for newline)"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent disabled:opacity-50 resize-none max-h-40 leading-5"
              style={{ minHeight: "44px" }}
            />
            <VoiceRecorder
              caseId={caseId}
              disabled={sending}
              onTranscribed={(text) => {
                // Append to whatever is already in the box — user reviews & edits.
                setInput((cur) => (cur ? `${cur} ${text}` : text));
              }}
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={sending || !input.trim()}
              className="bg-[#C5283F] text-white px-4 py-3 rounded-lg hover:bg-[#A82035] disabled:opacity-50 inline-flex items-center gap-1.5 text-sm font-medium"
            >
              <Send size={14} /> Send
            </button>
          </form>
          <div className="text-[11px] text-slate-400 mt-2 hidden sm:flex items-center gap-2">
            <FileImage size={11} /> Infi will ask to upload PAN & Aadhaar when adding directors. Tap the mic to speak — your audio is transcribed and discarded immediately.
          </div>
        </div>
      </div>

      {/* CASE FILE PANE (desktop) */}
      <div className="hidden md:block md:w-2/5 bg-white overflow-y-auto">
        {isPostPaid(caseData) ? (
          <FilingTrackerPanel caseData={caseData} />
        ) : (
          <CaseFilePanel caseData={caseData}
                         onPay={() => launchStripeCheckout({
                           caseId,
                           onError: (msg) => setError(msg),
                         })} />
        )}
      </div>

      {/* CASE FILE DRAWER (mobile) */}
      <div className={["md:hidden case-drawer", drawerOpen ? "open" : "peek",
                        "fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-[0_-12px_40px_rgba(0,0,0,0.12)] z-40 max-h-[88vh] overflow-hidden"
                      ].join(" ")}
           data-testid="case-drawer">
        <button
          onClick={() => setDrawerOpen((v) => !v)}
          data-testid="drawer-toggle"
          className="w-full h-16 flex flex-col items-center justify-center pt-2"
        >
          <div className="w-12 h-1.5 bg-slate-300 rounded-full mb-1.5" />
          <div className="text-xs text-slate-700 font-medium flex items-center gap-1">
            <ChevronUp
              size={12}
              className={["transition-transform", drawerOpen ? "rotate-180" : ""].join(" ")}
            />
            {drawerOpen ? "Hide" : "Show"} case file
          </div>
        </button>
        <div className="max-h-[calc(88vh-64px)] overflow-y-auto">
          {isPostPaid(caseData) ? (
            <FilingTrackerPanel caseData={caseData} />
          ) : (
            <CaseFilePanel caseData={caseData}
                           onPay={() => launchStripeCheckout({
                             caseId,
                             onError: (msg) => setError(msg),
                           })} />
          )}
        </div>
      </div>

      {inviteModalOpen && (
        <InviteManagerModal caseId={caseId} onClose={() => {
          setInviteModalOpen(false);
          // refresh case (a new stakeholder slot may have been created)
          api.getCase(caseId).then(setCaseData).catch(() => {});
        }} />
      )}
    </div>
  );
}
